import { VORLADEN_GESAMT, VORLADEN_JE_DATEI, fuerImmerWeg, medienSchluessel, vorladeListe } from '@schnitt/shared';
import type { AssetView } from './api';
import { ausDemRamWerfen, inDenRam } from './ramvorrat';

/**
 * Die Vorschau-Dateien vorladen (7.69n).
 *
 * Sascha, 9. September: „ganz oft nur ein FPS, aber beim zweiten Durchlauf
 * dann flüssig“. Der Befund vom Telefon sagt, warum: beim ersten Durchlauf
 * stand die Videoebene sekundenlang bei „nur Kopfdaten“ -- die Datei kam
 * nicht schnell genug aus dem Netz. Beim zweiten Durchlauf lag sie im
 * Zwischenspeicher. Also wird der zweite Durchlauf zum ersten gemacht:
 * sobald ein Projekt offen ist, holt die App die Vorschau-Dateien im
 * Hintergrund in den Cache-Speicher, und der Service Worker beantwortet die
 * Anfragen des Videoelements von dort -- samt Bereichsanfragen (Range), die
 * iOS für Video braucht.
 *
 * Nacheinander, nicht alle auf einmal, und in der Reihenfolge, in der die
 * Dateien im Schnitt drankommen. Eine Obergrenze hält den Speicher klein:
 * was am längsten nicht gebraucht wurde, fliegt zuerst heraus.
 */
export const VORSCHAU_SPEICHER = 'schnitt-vorschau-v1';
/** Mehr als das je Datei wird nicht vorgeladen -- ein langes Rohmaterial lädt weiter aus dem Netz. */
const HOECHSTENS_JE_DATEI = VORLADEN_JE_DATEI;
/** Und mehr als das insgesamt nicht; darüber fliegt das Älteste heraus. */
const HOECHSTENS_GESAMT = VORLADEN_GESAMT;
const INDEX = 'schnitt.vorschau.index';

interface Eintrag {
  bytes: number;
  zuletzt: number;
}

export interface VorladeStand {
  geladen: number;
  offen: number;
  fehler: number;
  /** Adressen, die es nicht mehr gibt -- gelöschte Medien (7.83). */
  verschollen: number;
  bytes: number;
  laeuft: boolean;
  /** Warum es steht: kein Speicher, Platz voll -- oder nichts. */
  grund: string;
}

const stand: VorladeStand = { geladen: 0, offen: 0, fehler: 0, verschollen: 0, bytes: 0, laeuft: false, grund: '' };
let abbruch: AbortController | null = null;
/**
 * Adressen, die es nicht mehr gibt (7.83).
 *
 * Wird ein Medium gelöscht, während ein anderes Fenster seine Liste noch
 * hält, holt dessen Vorladen eine Datei, die weg ist. Das eine 404 ist
 * richtig -- anders erfährt das Fenster es nicht. Das zweite wäre der
 * Fehler: dieselbe Adresse würde bei jedem Projektwechsel wieder geholt.
 * Also wird sie hier vermerkt, und zwar still: ein gelöschtes Medium ist
 * kein Fehlschlag, sondern eine Entscheidung, die jemand getroffen hat.
 */
const verschollen = new Set<string>();
/** Gemerkt wird der Pfad, nicht die Adresse: die Marke wird jedes Mal neu ausgestellt. */
function merkeVerschollen(url: string): void {
  verschollen.add(medienSchluessel(url));
}

function liesIndex(): Record<string, Eintrag> {
  try {
    return JSON.parse(localStorage.getItem(INDEX) ?? '{}') as Record<string, Eintrag>;
  } catch {
    return {};
  }
}
function schreibIndex(index: Record<string, Eintrag>): void {
  try {
    localStorage.setItem(INDEX, JSON.stringify(index));
  } catch {
    /* ohne Speicher kein Index -- dann wird beim nächsten Mal neu geladen */
  }
}

function speicherDa(): boolean {
  return typeof caches !== 'undefined' && typeof window !== 'undefined';
}

/**
 * Welche Dateien es zu laden gibt, in der Reihenfolge ihres ersten Auftritts
 * im Schnitt. Die Regel steht im gemeinsamen Teil (`vorladeListe`) und ist
 * dort geprüft; hier steht nur, mit welchem Gedächtnis sie gefragt wird.
 */
export function ladeliste(assets: AssetView[], reihenfolge: string[]): AssetView[] {
  return vorladeListe(assets, reihenfolge, verschollen, HOECHSTENS_JE_DATEI);
}

/**
 * Diese Adressen sind weg -- aus dem Zwischenspeicher, aus dem
 * Arbeitsspeicher, aus dem Index, und für den Rest der Sitzung aus jeder
 * Ladeliste.
 *
 * Aufgerufen wird das dort, wo jemand ein Medium wegwirft: dann zieht die
 * Liste mit, statt gleich darauf ins Leere zu greifen.
 */
export async function vergiss(adressen: readonly (string | null | undefined)[]): Promise<void> {
  const liste = adressen.filter((a): a is string => !!a);
  if (liste.length === 0) return;
  for (const url of liste) {
    merkeVerschollen(url);
    ausDemRamWerfen(url);
  }
  // Ein laufender Durchgang hat die alte Liste in der Hand; er wird beim
  // nächsten Anstoss ohnehin abgebrochen, aber bis dahin soll er nicht noch
  // eine Datei holen, die es nicht mehr gibt.
  abbruch?.abort();
  if (!speicherDa()) return;
  // Nach Pfad, nicht nach Adresse: im Zwischenspeicher kann dieselbe Datei
  // unter einer älteren Marke liegen als in der Liste, die gerade offen ist.
  const schluessel = new Set(liste.map(medienSchluessel));
  try {
    const cache = await caches.open(VORSCHAU_SPEICHER);
    for (const eintrag of await cache.keys()) {
      if (schluessel.has(medienSchluessel(eintrag.url))) await cache.delete(eintrag);
    }
    const index = liesIndex();
    for (const url of Object.keys(index)) {
      if (schluessel.has(medienSchluessel(url))) delete index[url];
    }
    schreibIndex(index);
  } catch {
    /* kein Speicher: dann gibt es auch nichts wegzuräumen */
  }
}


/** Ältestes zuerst hinaus, bis unter der Grenze. */
async function platzSchaffen(cache: Cache, index: Record<string, Eintrag>, dazu: number): Promise<void> {
  let summe = Object.values(index).reduce((s, e) => s + e.bytes, 0) + dazu;
  const aelteste = Object.entries(index).sort((a, b) => a[1].zuletzt - b[1].zuletzt);
  for (const [url, e] of aelteste) {
    if (summe <= HOECHSTENS_GESAMT) break;
    await cache.delete(url);
    delete index[url];
    summe -= e.bytes;
  }
}

/**
 * Und die gerechneten Abschnitte (7.69t).
 *
 * Der Befund vom iPhone, 11. September, 01:09: `takt: film`, acht Sekunden
 * Wartezeit mit `abgelaufen: 1` -- und daneben „8 Dateien vorgeladen“. Die
 * *Medien* lagen also bereit, der vorgerechnete **Film** nicht. Genau die
 * Datei, die beim Abspielen zählt, wurde kalt aus dem Netz geholt: sie ist
 * kein Medium, sondern ein Ergebnis, und das Vorladen kannte nur Medien.
 *
 * Hier ist sie eine Adresse wie jede andere: in den Zwischenspeicher und
 * weiter in den Arbeitsspeicher (7.69s), nacheinander, mit derselben Grenze.
 * Was schon liegt, kostet nichts.
 */
export async function ladeFilmeVor(urls: readonly string[]): Promise<number> {
  if (!speicherDa() || urls.length === 0) return 0;
  let geladen = 0;
  try {
    const cache = await caches.open(VORSCHAU_SPEICHER);
    const index = liesIndex();
    for (const url of urls) {
      if (verschollen.has(medienSchluessel(url))) continue;
      const schon = await cache.match(url);
      if (!schon) {
        try {
          const antwort = await fetch(url, { cache: 'no-store' });
          if (!antwort.ok) {
            if (fuerImmerWeg(antwort.status)) merkeVerschollen(url);
            continue;
          }
          const bytes = Number(antwort.headers.get('content-length') ?? 0);
          if (bytes > HOECHSTENS_JE_DATEI) {
            await antwort.body?.cancel();
            continue;
          }
          await platzSchaffen(cache, index, bytes);
          await cache.put(url, antwort);
          index[url] = { bytes, zuletzt: Date.now() };
        } catch {
          continue;
        }
      } else {
        index[url] = { bytes: index[url]?.bytes ?? 0, zuletzt: Date.now() };
      }
      // Und weiter in den Arbeitsspeicher -- ohne Gebrauchs-Stempel, wie beim
      // Material: gebraucht wird sie, wenn sie gespielt wird.
      await inDenRam(url, false);
      geladen += 1;
    }
    schreibIndex(index);
  } catch {
    /* kein Speicher, keine Erlaubnis: dann läuft es wie vorher */
  }
  return geladen;
}

/**
 * Vorladen anstossen. Ein zweiter Aufruf bricht den ersten ab und fängt mit
 * der neuen Liste an -- beim Wechsel des Projekts etwa.
 */
export async function ladeVor(assets: AssetView[], reihenfolge: string[] = []): Promise<void> {
  if (!speicherDa()) {
    stand.grund = 'kein Cache-Speicher';
    return;
  }
  abbruch?.abort();
  const eigener = new AbortController();
  abbruch = eigener;
  const liste = ladeliste(assets, reihenfolge);
  stand.laeuft = true;
  stand.grund = '';
  stand.offen = liste.length;
  try {
    const cache = await caches.open(VORSCHAU_SPEICHER);
    const index = liesIndex();
    for (const asset of liste) {
      if (eigener.signal.aborted) return;
      const url = asset.proxyUrl!;
      // Die Liste ist beim Anstoss gefiltert; verschollen werden kann eine
      // Adresse aber auch noch, während die Schleife läuft.
      if (verschollen.has(medienSchluessel(url))) {
        stand.offen = Math.max(0, stand.offen - 1);
        continue;
      }
      const schon = await cache.match(url);
      if (schon) {
        index[url] = { bytes: index[url]?.bytes ?? asset.sizeBytes, zuletzt: Date.now() };
        stand.offen--;
        stand.geladen++;
        // Liegt sie schon im Zwischenspeicher, kostet der Weg in den
        // Arbeitsspeicher kein einziges Byte über die Leitung (7.69s) -- und
        // von dort liest das Videoelement, ohne beim Abspielen nachzuholen.
        //
        // `false`: vorladen heisst nicht gebrauchen. Sonst gälte jede Datei
        // eines Projekts eine halbe Minute als „in Gebrauch“, und der Vorrat
        // könnte nichts verdrängen -- ausgerechnet für den Clip, zu dem
        // jemand gerade springt, wäre dann kein Platz.
        await inDenRam(url, false);
        continue;
      }
      try {
        const antwort = await fetch(url, { signal: eigener.signal, cache: 'no-store' });
        if (!antwort.ok) {
          // Weg ist weg: einmal fragen genügt, danach steht die Adresse auf
          // der Liste der Verschollenen und kostet nichts mehr. Und es ist
          // kein Fehler -- jemand hat das Medium gelöscht.
          if (fuerImmerWeg(antwort.status)) {
            merkeVerschollen(url);
            stand.verschollen++;
            continue;
          }
          throw new Error(`${antwort.status}`);
        }
        const bytes = Number(antwort.headers.get('content-length') ?? 0);
        if (bytes > HOECHSTENS_JE_DATEI) {
          await antwort.body?.cancel();
          stand.offen--;
          continue;
        }
        await platzSchaffen(cache, index, bytes);
        await cache.put(url, antwort);
        index[url] = { bytes, zuletzt: Date.now() };
        stand.geladen++;
        stand.bytes += bytes;
        // Und gleich weiter in den Arbeitsspeicher: die Datei liegt jetzt im
        // Zwischenspeicher, der zweite Weg ist ein Griff ins Regal. Auch hier
        // ohne Gebrauchs-Stempel.
        await inDenRam(url, false);
      } catch (err) {
        if (eigener.signal.aborted) return;
        stand.fehler++;
        // Voll: dann ist hier Schluss, und die Wiedergabe liest wie bisher aus dem Netz.
        if (err instanceof Error && /quota/i.test(err.name + err.message)) {
          stand.grund = 'Speicher voll';
          return;
        }
      } finally {
        if (!eigener.signal.aborted) stand.offen = Math.max(0, stand.offen - 1);
      }
    }
    schreibIndex(index);
  } finally {
    if (abbruch === eigener) stand.laeuft = false;
  }
}

/** Für die Diagnose und den Befund: wie weit das Vorladen ist. */
export function vorladeStand(): VorladeStand {
  return { ...stand };
}

declare global {
  interface Window {
    __vorgeladen?: () => string;
  }
}
if (typeof window !== 'undefined') window.__vorgeladen = () => JSON.stringify(vorladeStand());
